/**
 * Centrale pakkettenlijst — enige bron van waarheid voor:
 *  - de CTA-knoppen bij de pakketten (`/contact?pakket=<id>`)
 *  - de "gekozen pakket"-kaart boven het contactformulier
 *  - de offerte-bevestigingsmail en de interne mail
 *
 * Prijzen komen uitsluitend hier vandaan (server-side gevalideerd),
 * nooit uit de URL.
 */

export type PackageLang = "nl" | "fr" | "en";

export type PackageDef = {
  id: string;
  name: string;
  price: string;
  /** "/jaar" | "eenmalig" — per taal */
  suffix: Record<PackageLang, string>;
  badge: Record<PackageLang, string>;
  subPrice?: Record<PackageLang, string>;
  /** Verplichte extra die als aparte regel op de kaart komt (niet als subprijs). */
  addOn?: Record<PackageLang, string>;
  /** Tijdelijk introductietarief (los bedrag, voor gebruik in copy) */
  introPrice?: string;
  /** Einddatum introductietarief als ISO-datum ("2026-12-31"); na deze dag geldt weer `price`. */
  introUntil?: string;
  /** Einddatum introductietarief, gelokaliseerd */
  introDeadline?: Record<PackageLang, string>;
  /** 4 kernfeatures, kort, per taal */
  features: Record<PackageLang, string[]>;
};


export const PACKAGES: PackageDef[] = [
  {
    id: "sport-club",
    name: "Sport & Club",
    price: "€749",
    suffix: { nl: "/jaar", fr: "/an", en: "/year" },
    badge: {
      nl: "Speciale Actie · Verenigingen",
      fr: "Offre spéciale · Associations",
      en: "Special offer · Clubs & non-profits",
    },
    subPrice: {
      nl: "Verplichte minimale looptijd: 36 maanden",
      fr: "Durée minimale obligatoire : 36 mois",
      en: "Mandatory minimum term: 36 months",
    },
    features: {
      nl: [
        "Volledige clubwebsite met sector-template",
        "Kalender, nieuws en ploegpagina's",
        "Hosting, onderhoud en updates inbegrepen (t.w.v. €495/jaar)",
        "Gereduceerd tarief voor vzw's en non-profits",
      ],
      fr: [
        "Site de club complet avec template sectoriel",
        "Calendrier, actualités et pages d'équipes",
        "Hébergement, maintenance et mises à jour inclus (valeur de 495 €/an)",
        "Tarif réduit pour ASBL et non-profits",
      ],
      en: [
        "Complete club website on a sector template",
        "Calendar, news and team pages",
        "Hosting, maintenance and updates included (worth €495/year)",
        "Reduced rate for non-profit organisations",
      ],
    },
  },
  {
    id: "local-hero-basic",
    name: "Local Hero Basic",
    price: "€990",
    suffix: { nl: "/jaar", fr: "/an", en: "/year" },
    badge: {
      nl: "Meest Gekozen · Lokale KMO's",
      fr: "Le plus choisi · PME locales",
      en: "Most chosen · Local SMEs",
    },
    subPrice: {
      nl: "Verplichte minimale looptijd: 36 maanden",
      fr: "Durée minimale obligatoire : 36 mois",
      en: "Mandatory minimum term: 36 months",
    },
    addOn: {
      nl: "+ €495 / jaar — Cloud & onderhoud: domeinnaam, hosting en security updates",
      fr: "+ 495 € / an — Cloud & maintenance : nom de domaine, hébergement et mises à jour de sécurité",
      en: "+ €495 / year — Cloud & maintenance: domain name, hosting and security updates",
    },
    features: {
      nl: [
        "5 structurele pagina's op sector-template",
        "Enterprise performance- en securitylaag",
        "Jaarlijkse PageSpeed-check",
        "Maandelijkse SEO-scan en rapport",
      ],
      fr: [
        "5 pages structurelles sur template sectoriel",
        "Couche performance et sécurité enterprise",
        "Contrôle PageSpeed annuel",
        "Scan SEO mensuel et rapport",
      ],
      en: [
        "5 structural pages on a sector template",
        "Enterprise performance and security layer",
        "Annual PageSpeed check",
        "Monthly SEO scan and report",
      ],
    },
  },
  {
    id: "local-hero-premium",
    name: "Local Hero Premium",
    price: "€1.490",
    suffix: { nl: "/jaar", fr: "/an", en: "/year" },
    badge: {
      nl: "Maximale Operationele Waarde",
      fr: "Valeur opérationnelle maximale",
      en: "Maximum operational value",
    },
    subPrice: {
      nl: "Verplichte minimale looptijd: 36 maanden",
      fr: "Durée minimale obligatoire : 36 mois",
      en: "Mandatory minimum term: 36 months",
    },
    addOn: {
      nl: "+ €495 / jaar — Cloud & onderhoud: domeinnaam, hosting en security updates",
      fr: "+ 495 € / an — Cloud & maintenance : nom de domaine, hébergement et mises à jour de sécurité",
      en: "+ €495 / year — Cloud & maintenance: domain name, hosting and security updates",
    },
    features: {
      nl: [
        "Tot 10 pagina's met sector-templates",
        "Automatisering van aanvragen en bestellingen",
        "Enterprise performance- en securitylaag",
        "Maandelijkse SEO-scan en rapport",
      ],
      fr: [
        "Jusqu'à 10 pages avec templates sectoriels",
        "Automatisation des demandes et commandes",
        "Couche performance et sécurité enterprise",
        "Scan SEO mensuel et rapport",
      ],
      en: [
        "Up to 10 pages with sector templates",
        "Automation of requests and orders",
        "Enterprise performance and security layer",
        "Monthly SEO scan and report",
      ],
    },
  },
  {
    id: "seo-geo-audit",
    name: "SEO, GEO & AI-analyse",
    price: "€995",
    suffix: { nl: "eenmalig", fr: "paiement unique", en: "one-off" },
    badge: {
      nl: "Los bestelbaar · geen bouwverplichting",
      fr: "Commande séparée · sans obligation de construction",
      en: "Order separately · no build commitment",
    },
    subPrice: {
      nl: "Verrekend als u daarna met ons bouwt",
      fr: "Déduit si vous construisez ensuite avec nous",
      en: "Credited if you build with us afterwards",
    },
    features: {
      nl: [
        "Volledige SEO-, GEO- en AI-zichtbaarheidsanalyse van uw domein",
        "Rapport met bevindingen, prioriteiten en concreet actieplan",
        "Technische check: Core Web Vitals, indexatie en structured data",
        "Doorlooptijd tien werkdagen — zonder verdere verplichtingen",
      ],
      fr: [
        "Analyse complète de visibilité SEO, GEO et IA de votre domaine",
        "Rapport avec constats, priorités et plan d'action concret",
        "Contrôle technique : Core Web Vitals, indexation et données structurées",
        "Délai de dix jours ouvrables — sans autre engagement",
      ],
      en: [
        "Full SEO, GEO and AI visibility analysis of your domain",
        "Report with findings, priorities and a concrete action plan",
        "Technical check: Core Web Vitals, indexing and structured data",
        "Ten working days turnaround — with no further commitment",
      ],
    },
  },
  {
    id: "business-architectuur",
    name: "Business Architectuur",
    price: "€9.950",
    suffix: { nl: "eenmalig", fr: "paiement unique", en: "one-off" },
    badge: { nl: "KMO Standaard", fr: "Standard PME", en: "SME standard" },
    subPrice: {
      nl: "+ €750 / jaar verplichte Managed Cloud & Hosting Fee",
      fr: "+ 750 € / an de Managed Cloud & Hosting Fee obligatoire",
      en: "+ €750 / year mandatory Managed Cloud & Hosting fee",
    },
    features: {
      nl: [
        "100% unieke custom layout — geen templates",
        "Modulaire pagebuilder met 15+ componenten",
        "Enterprise performance- & security-suite",
        "Hosting op Europese servers (GDPR)",
      ],
      fr: [
        "Design 100 % sur mesure — sans templates",
        "Page builder modulaire avec 15+ composants",
        "Suite performance & sécurité enterprise",
        "Hébergement sur serveurs européens (RGPD)",
      ],
      en: [
        "100% custom layout — no templates",
        "Modular page builder with 15+ components",
        "Enterprise performance & security suite",
        "Hosting on European servers (GDPR)",
      ],
    },
  },
  {
    id: "pro-marketeer",
    name: "Geïntegreerde Pro Marketeer",
    price: "€19.950",
    suffix: { nl: "eenmalig", fr: "paiement unique", en: "one-off" },
    badge: { nl: "Populair — Harde Groei", fr: "Populaire — Croissance", en: "Popular — Hard growth" },
    subPrice: {
      nl: "+ €1.500 / jaar verplichte Managed Cloud & Headless CMS Fee",
      fr: "+ 1.500 € / an de Managed Cloud & Headless CMS Fee obligatoire",
      en: "+ €1,500 / year mandatory Managed Cloud & headless CMS fee",
    },
    features: {
      nl: [
        "Echt headless CMS met realtime preview",
        "Tot 2 actieve enterprise API-koppelingen",
        "Smart routing, zero-downtime deployment",
        "MCP enabled — klaar voor AI-agents",
      ],
      fr: [
        "Véritable CMS headless avec preview temps réel",
        "Jusqu'à 2 intégrations API enterprise actives",
        "Smart routing, déploiement sans interruption",
        "MCP enabled — prêt pour les agents IA",
      ],
      en: [
        "True headless CMS with realtime preview",
        "Up to 2 active enterprise API integrations",
        "Smart routing, zero-downtime deployment",
        "MCP enabled — ready for AI agents",
      ],
    },
  },
  {
    id: "corporate-enterprise",
    name: "Corporate Enterprise",
    price: "Vanaf €34.950",
    suffix: { nl: "eenmalig", fr: "paiement unique", en: "one-off" },
    badge: { nl: "Midmarket & Security", fr: "Midmarket & sécurité", en: "Midmarket & security" },
    subPrice: {
      nl: "Enterprise SLA & support — prijs op aanvraag",
      fr: "SLA & support enterprise — prix sur demande",
      en: "Enterprise SLA & support — price on request",
    },
    features: {
      nl: [
        "100% intellectueel eigendom (IP-transfer)",
        "Tot 4 zware enterprise integraties",
        "Enterprise SSO + OWASP & GDPR/ISO-audits",
        "Multi-region EU data-residency",
      ],
      fr: [
        "100 % de propriété intellectuelle (transfert IP)",
        "Jusqu'à 4 intégrations enterprise lourdes",
        "SSO enterprise + audits OWASP & RGPD/ISO",
        "Résidence des données multi-région UE",
      ],
      en: [
        "100% intellectual property (IP transfer)",
        "Up to 4 heavy enterprise integrations",
        "Enterprise SSO + OWASP & GDPR/ISO audits",
        "Multi-region EU data residency",
      ],
    },
  },
  {
    id: "second-opinion",
    name: "Onafhankelijke beoordeling",
    price: "€2.250",
    suffix: { nl: "eenmalig", fr: "eenmalig", en: "eenmalig" },
    badge: {
      nl: "Maximaal 3 dossiers per maand",
      fr: "Maximaal 3 dossiers per maand",
      en: "Maximaal 3 dossiers per maand",
    },
    introPrice: "€1.750",
    introUntil: "2026-12-31",
    introDeadline: {
      nl: "31 december 2026",
      fr: "31 décembre 2026",
      en: "31 December 2026",
    },
    subPrice: {
      nl: "Introductietarief €1.750 tot en met 31 december 2026",
      fr: "Introductietarief €1.750 tot en met 31 december 2026",
      en: "Introductietarief €1.750 tot en met 31 december 2026",
    },
    features: {
      nl: [
        "Onafhankelijke lezing van uw eigen meetgegevens",
        "Rapport van dertig tot veertig pagina's met bronvermelding",
        "Actieplan op prioriteit plus vragenlijst voor uw leverancier",
        "Gesprek van anderhalf uur om alles door te nemen",
      ],
      fr: [
        "Onafhankelijke lezing van uw eigen meetgegevens",
        "Rapport van dertig tot veertig pagina's met bronvermelding",
        "Actieplan op prioriteit plus vragenlijst voor uw leverancier",
        "Gesprek van anderhalf uur om alles door te nemen",
      ],
      en: [
        "Onafhankelijke lezing van uw eigen meetgegevens",
        "Rapport van dertig tot veertig pagina's met bronvermelding",
        "Actieplan op prioriteit plus vragenlijst voor uw leverancier",
        "Gesprek van anderhalf uur om alles door te nemen",
      ],
    },
  },
];


/** Is het introductietarief vandaag nog actief? (Brussel-tijd, einddag telt mee.) */
export function isIntroActive(pkg: PackageDef, now: Date = new Date()): boolean {
  if (!pkg.introPrice || !pkg.introUntil) return false;
  const end = new Date(`${pkg.introUntil}T23:59:59+01:00`);
  return now.getTime() <= end.getTime();
}

/** Actieve prijs: introductietarief tijdens de actieperiode, anders de standaardprijs. */
export function getActivePrice(pkg: PackageDef, now?: Date): string {
  return isIntroActive(pkg, now) ? pkg.introPrice! : pkg.price;
}

/** Numeriek introductievoordeel ("€2.250" - "€1.750" → 500), of null als geen actie loopt. */
export function getIntroDiscount(pkg: PackageDef, now?: Date): number | null {
  if (!isIntroActive(pkg, now)) return null;
  const num = (s: string) => Number(s.replace(/[^\d]/g, ""));
  return num(pkg.price) - num(pkg.introPrice!);
}

export function getPackage(id: string | undefined | null): PackageDef | undefined {
  if (!id) return undefined;
  return PACKAGES.find((p) => p.id === id);
}

export function packageLang(lang: string | undefined): PackageLang {
  return lang === "fr" || lang === "en" ? lang : "nl";
}
/** SEO-traject (add-on): enige bron van waarheid voor prijs en voorwaarden. */
export const SEO_ADDON = {
  monthlyPrice: 595,
  extraBlogPrice: 145,
  blogsIncludedPerMonth: 1,
  minimumMonths: 6,
  billing: "quarterly_in_advance",
  standardScope: { regions: 1, maxPages: 20 },
} as const;

export function formatEuro(n: number): string {
  return `€${n.toLocaleString("nl-BE")}`;
}
